import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink } from 'lucide-react';
import Overview from './recruitment/Overview';
import Pipeline from './recruitment/Pipeline';
import Reports from './recruitment/Reports';
import Rubrics from './recruitment/Rubrics';
import ItemBank from './recruitment/ItemBank';
import Roles from './recruitment/Roles';
import CandidateProfile from './recruitment/CandidateProfile';

const API_URL = import.meta.env.VITE_API_URL;
const HIRING_SITE_URL = 'https://hiring.ellasplace.net';
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'reports', label: 'Reports' },
  { key: 'roles', label: 'Roles' },
  { key: 'rubrics', label: 'Rubrics' },
  { key: 'item-bank', label: 'Questions' },
];

function Recruitment() {
  const [activeTab, setActiveTab] = useState('overview');
  const [rubrics, setRubrics] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link support: every "View Full Scorecard" button in a CEO stage-report
  // email points at /recruitment?applicationId=<id>, so this opens straight to
  // that candidate instead of landing on a bare list they'd have to search.
  const [selectedApplicationId, setSelectedApplicationId] = useState(searchParams.get('applicationId'));
  const [refreshKey, setRefreshKey] = useState(0);

  const loadRubrics = useCallback(() => {
    axios.get(`${API_URL}/api/recruitment/rubrics`).then((res) => setRubrics(res.data.rubrics)).catch(() => {});
  }, []);

  useEffect(() => { loadRubrics(); }, [loadRubrics]);

  const openCandidate = (id) => {
    setSelectedApplicationId(id);
    setSearchParams({ applicationId: id });
  };

  const closeCandidate = () => {
    setSelectedApplicationId(null);
    setSearchParams({});
    setRefreshKey((k) => k + 1); // force Overview/Pipeline to refetch after a review/decision
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Recruitment</h1>
          <p className="text-sm text-gray-500">Every candidate&apos;s pipeline, scorecards, and reviews.</p>
        </div>
        <a
          href={HIRING_SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-2 rounded-lg shrink-0"
        >
          View Hiring Site <ExternalLink size={14} />
        </a>
      </div>

      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-4">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {activeTab === 'overview' && <Overview key={refreshKey} onOpenCandidate={openCandidate} />}
      {activeTab === 'pipeline' && <Pipeline key={refreshKey} onOpenCandidate={openCandidate} />}
      {activeTab === 'reports' && <Reports key={refreshKey} />}
      {activeTab === 'roles' && <Roles />}
      {activeTab === 'rubrics' && <Rubrics rubrics={rubrics} onSaved={loadRubrics} />}
      {activeTab === 'item-bank' && <ItemBank rubrics={rubrics} />}

      {selectedApplicationId && (
        <CandidateProfile
          applicationId={selectedApplicationId}
          rubrics={rubrics}
          onClose={closeCandidate}
        />
      )}
    </div>
  );
}

export default Recruitment;
